import { PageHero, SectionHeader } from "@/components/Section";
import useReveal from "@/hooks/useReveal";
import { church, fullAddress } from "@/config/church";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Clock, Car, Baby, Shirt, HelpCircle, ArrowRight } from "lucide-react";

const expect = [
  { icon: Clock, title: "Warm Welcome", body: "Greeters meet you at the door and can walk you to the sanctuary or kids' area." },
  { icon: Baby, title: "Kids Ministry", body: "Safe, secure children's check-in with age-appropriate biblical teaching every Sunday." },
  { icon: Shirt, title: "Come As You Are", body: "There is no dress code. Some dress up, some dress casually — you'll fit right in." },
  { icon: Car, title: "Easy Parking", body: "Free parking on-site with guest-friendly signage and volunteers ready to assist." },
];

const faqs = [
  { q: "What time do services start?", a: "Sunday School begins at 10:00 AM and Morning Worship at 11:00 AM. Wednesday Bible Study is at 7:00 PM." },
  { q: "How long is the service?", a: "Sunday worship is typically about 90 minutes — expect Spirit-filled worship, prayer, and biblical preaching from Pastor Darren M. Little." },
  { q: "Is childcare provided?", a: "Yes! We have secure, joyful children's environments for babies through elementary during our 11 AM service." },
  { q: "How do I get involved after visiting?", a: "Stop by our Welcome Table after service, or fill out the connect form on this page — we'll follow up personally." },
];

export default function Visit() {
  useReveal();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", visit_date: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) return;
    try {
      setBusy(true);
      await api.post("/registration", {
        kind: "visit",
        name: form.name,
        email: form.email || null,
        phone: form.phone || null,
        details: { visit_date: form.visit_date },
        notes: form.notes || null,
      });
      setForm({ name: "", email: "", phone: "", visit_date: "", notes: "" });
      navigate("/thank-you?source=visit");
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="visit-page">
      <PageHero
        eyebrow="Plan Your Visit"
        title="We can't wait to meet you."
        subtitle="Everything a first-time guest needs to know — service times, what to expect, kids' check-in, parking, and directions."
        image="https://customer-assets-eiarnc6j.emergentagent.net/job_strange-cartwright-8/artifacts/vxoldu53_616955902_122105707497216091_3578243584108528149_n.jpg"
      />

      {/* Service Times */}
      <section className="bg-white py-24">
        <div className="container-npbc grid lg:grid-cols-[1.1fr_1fr] gap-14 items-start">
          <div data-reveal>
            <div className="eyebrow">Service Times</div>
            <h2 className="mt-3 heading-serif text-5xl leading-tight">When we gather.</h2>
            <div className="mt-8 space-y-4">
              {church.services.map((s) => (
                <div key={s.name} className="rounded-2xl border border-black/[0.06] bg-npbc-cream p-6 flex items-center justify-between">
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-npbc-red">{s.day}</div>
                    <div className="mt-1 heading-serif text-2xl">{s.name}</div>
                  </div>
                  <div className="heading-serif text-3xl text-npbc-black">{s.time}</div>
                </div>
              ))}
            </div>

            <div className="mt-10 rounded-2xl border border-black/[0.06] p-6">
              <div className="flex items-center gap-3 text-npbc-black">
                <MapPin className="h-5 w-5 text-npbc-red" />
                <div className="heading-serif text-xl">{fullAddress}</div>
              </div>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="visit-directions-btn"
                className="btn-ghost-dark mt-5"
              >
                Get Directions <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div data-reveal className="card-elevated p-8 md:p-10">
            <div className="eyebrow">Tell Us You're Coming</div>
            <h3 className="mt-3 heading-serif text-3xl leading-tight">Let us roll out the red carpet.</h3>
            <p className="mt-2 text-npbc-gray">First-time guests get a warm greeting and a special welcome gift.</p>
            <form onSubmit={submit} className="mt-6 space-y-3">
              <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="visit-form-name" className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm outline-none focus:border-npbc-red focus:ring-2 focus:ring-npbc-red/20" />
              <div className="grid sm:grid-cols-2 gap-3">
                <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="visit-form-email" className="rounded-xl border border-black/10 bg-white px-4 py-3 text-sm outline-none focus:border-npbc-red focus:ring-2 focus:ring-npbc-red/20" />
                <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="visit-form-phone" className="rounded-xl border border-black/10 bg-white px-4 py-3 text-sm outline-none focus:border-npbc-red focus:ring-2 focus:ring-npbc-red/20" />
              </div>
              <input type="date" value={form.visit_date} onChange={(e) => setForm({ ...form, visit_date: e.target.value })} data-testid="visit-form-date" className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm outline-none focus:border-npbc-red focus:ring-2 focus:ring-npbc-red/20" />
              <textarea placeholder="Anything we should know? (family size, questions...)" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="visit-form-notes" className="w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-sm outline-none focus:border-npbc-red focus:ring-2 focus:ring-npbc-red/20" />
              <button type="submit" disabled={busy} data-testid="visit-form-submit" className="btn-primary w-full justify-center">
                {busy ? "Sending..." : "I'm Planning to Visit"} <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* What to expect */}
      <section className="bg-npbc-cream py-24">
        <div className="container-npbc">
          <div data-reveal>
            <SectionHeader eyebrow="What to Expect" title="Your first Sunday, made easy." />
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {expect.map((e, i) => (
              <div key={e.title} data-reveal style={{ transitionDelay: `${i * 60}ms` }} className="card-elevated p-8">
                <div className="h-11 w-11 rounded-2xl bg-npbc-red/10 text-npbc-red grid place-items-center">
                  <e.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 heading-serif text-xl">{e.title}</h3>
                <p className="mt-2 text-sm text-npbc-gray leading-relaxed">{e.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Look for this sign — actual church sign at 533 */}
      <section className="bg-white py-24">
        <div className="container-npbc grid gap-14 lg:grid-cols-[1fr_1.1fr] items-center">
          <div data-reveal>
            <div className="eyebrow">Look for This Sign</div>
            <h2 className="mt-3 heading-serif text-5xl leading-[1.05]">
              You'll know you're here.
            </h2>
            <p className="mt-6 text-npbc-gray leading-relaxed text-[17px]">
              Our blue-and-white church sign at <span className="font-semibold text-npbc-black">533 S. Mendenhall Road</span> greets
              every guest with the promise of Psalm 23 — <em>"The Lord Is My Shepherd."</em>
              Watch for it on the corner and pull right in.
            </p>
            <div className="mt-6 rounded-2xl bg-npbc-cream p-5 border border-black/[0.06]">
              <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-npbc-red">Our Weekly Rhythm</div>
              <ul className="mt-3 space-y-1.5 text-sm text-npbc-black">
                <li><span className="font-semibold">Sunday School</span> — 10:00 AM</li>
                <li><span className="font-semibold">Worship Service</span> — 11:00 AM</li>
                <li><span className="font-semibold">Bible Class</span> — Wednesday 6:00 PM</li>
              </ul>
            </div>
          </div>
          <div data-reveal className="relative">
            <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-npbc-red/10 to-npbc-gold/20 rotate-1" />
            <img
              src="https://customer-assets-eiarnc6j.emergentagent.net/job_strange-cartwright-8/artifacts/vp94qujn_IMG_5420.jpeg"
              alt="New Philadelphia Missionary Baptist Church street sign at 533 S. Mendenhall Rd"
              className="relative rounded-3xl w-full h-[520px] object-cover shadow-2xl"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Shareable Invitation Flyer */}
      <section className="bg-npbc-black text-white py-24">
        <div className="container-npbc grid gap-14 lg:grid-cols-[1fr_1fr] items-center">
          <div data-reveal>
            <div className="eyebrow !text-npbc-gold">You're Invited</div>
            <h2 className="mt-3 heading-serif text-5xl leading-[1.05]">
              Share the invitation.
            </h2>
            <p className="mt-5 text-white/75 leading-relaxed text-[17px]">
              Know someone who could use a warm word, a family in Christ, or a fresh start?
              Send them this invitation. Everyone is welcome at New Philadelphia — come as you are.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="https://customer-assets-eiarnc6j.emergentagent.net/job_strange-cartwright-8/artifacts/9mirgyyi_616808285_122102915913216091_622664071237135384_n.jpg"
                download="new-philadelphia-invite.jpg"
                target="_blank"
                rel="noreferrer noopener"
                data-testid="visit-download-invite"
                className="btn-gold"
              >
                Download Invitation <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href={`sms:?&body=You're invited to church at New Philadelphia Baptist! 10 AM Sunday School, 11 AM Worship — 533 S. Mendenhall Rd, Memphis TN. https://newphiladelphiabaptist.org`}
                data-testid="visit-share-invite"
                className="btn-outline"
              >
                Share via Text
              </a>
            </div>
          </div>
          <div data-reveal className="relative mx-auto max-w-[380px]">
            <div className="absolute -inset-4 bg-gradient-to-br from-npbc-red/30 to-npbc-gold/30 rounded-3xl blur-2xl" />
            <img
              src="https://customer-assets-eiarnc6j.emergentagent.net/job_strange-cartwright-8/artifacts/9mirgyyi_616808285_122102915913216091_622664071237135384_n.jpg"
              alt="You're Invited to New Philadelphia Missionary Baptist Church"
              className="relative w-full rounded-3xl shadow-2xl"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Map + FAQ */}
      <section className="bg-white py-24">
        <div className="container-npbc grid lg:grid-cols-2 gap-10">
          <div data-reveal className="rounded-3xl overflow-hidden border border-black/[0.06] h-[440px]">
            <iframe
              title="Church location"
              src={`https://www.google.com/maps?q=${encodeURIComponent(fullAddress)}&output=embed`}
              className="w-full h-full"
              loading="lazy"
            />
          </div>
          <div data-reveal>
            <div className="eyebrow">Frequently Asked</div>
            <h2 className="mt-3 heading-serif text-4xl leading-tight">Questions guests often ask.</h2>
            <div className="mt-6 divide-y divide-black/[0.06] border-t border-b border-black/[0.06]">
              {faqs.map((f) => (
                <details key={f.q} className="group py-5">
                  <summary className="flex items-start justify-between cursor-pointer list-none">
                    <span className="heading-serif text-lg pr-6">{f.q}</span>
                    <span className="mt-1 grid h-7 w-7 place-items-center rounded-full bg-npbc-red/10 text-npbc-red font-bold group-open:rotate-45 transition">+</span>
                  </summary>
                  <p className="mt-3 text-npbc-gray leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
